import React, { useEffect, useRef, useState } from 'react';
import PerfisAdicionais from '../perfis-adicionais/PerfisAdicionais';
import { updateUser, User, deleteUser, restoreUser, getUserById } from '../../../api/usersApi';
import { getUserIdFromToken } from '../../../views/user/utils/auth';
import { toast } from 'react-toastify';
import ConfirmModal from '../confirm/ConfirmModal';
import { useIsTi } from '../../../hooks/useUserType';
import VinculoNasajonDoUsuario from './VinculoNasajonDoUsuario';

interface UpdateOtherUserModalProps {
  isOpen: boolean;
  onCancel: () => void;
  user: User;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  isSidebarOpen: boolean;
}

type UserWithEditableCodes = User & {
  codigoVoluntario?: number | null;
  codigoRegional?: number | null;
};

const displayToEnumMap: Record<string, string> = {
  "Colaborador": "COLABORADOR",
  "Recepção": "RECEPCAO",
  "Chefe de Oficina": "CHEFE_DE_OFICINA",
  "Orçamento": "ORCAMENTO",
  "Compra": "COMPRA",
  "Estoque": "ESTOQUE",
  "Consultor": "CONSULTOR",
  "Regional": "REGIONAL",
  "Relacionamento": "RELACIONAMENTO",
  "Contac": "CONTAC",
  "Comissionamento": "COMISSIONAMENTO",
  "Administrador": "ADMIN",
  "Administração": "ADMINISTRACAO",
  "TI": "TI",
  "Rastreador": "RASTREADOR",
  "Eventos": "EVENTOS",
  "Inspetor": "INSPETOR",
  "Telão": "TELAO",
  "Gestão de Compras": "GESTAO_COMPRAS",

  "COLABORADOR": "COLABORADOR",
  "RECEPCAO": "RECEPCAO",
  "CHEFE_DE_OFICINA": "CHEFE_DE_OFICINA",
  "ORCAMENTO": "ORCAMENTO",
  "COMPRA": "COMPRA",
  "ESTOQUE": "ESTOQUE",
  "CONSULTOR": "CONSULTOR",
  "REGIONAL": "REGIONAL",
  "RELACIONAMENTO": "RELACIONAMENTO",
  "CONTAC": "CONTAC",
  "COMISSIONAMENTO": "COMISSIONAMENTO",
  "ADMIN": "ADMIN",
  "ADMINISTRACAO": "ADMINISTRACAO",
  "RASTREADOR": "RASTREADOR",
  "EVENTOS": "EVENTOS",
  "INSPETOR": "INSPETOR",
  "TELAO": "TELAO",
  "GESTAO_COMPRAS": "GESTAO_COMPRAS"
};

const normalizeUserType = (type: string | undefined | null): string => {
  if (!type) return '';
  return displayToEnumMap[type] || type;
};

const UpdateOtherUserModal: React.FC<UpdateOtherUserModalProps> = ({
  isOpen,
  onCancel,
  user,
  setUser,
  sidebarRef,
  isSidebarOpen: _isSidebarOpen,
}) => {
  const currentUser = user as UserWithEditableCodes;

  const [updatedUser, setUpdatedUser] = useState({
    fullName: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    codigoVoluntario: '',
    codigoRegional: '',
    userType: '',
  });

  /** at-81: perfis ALÉM do principal. O acesso do usuário é a união dos dois. */
  const [perfisAdicionais, setPerfisAdicionais] = useState<string[]>([]);

  const [isSaving, setIsSaving] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isConfirmDeactivateOpen, setIsConfirmDeactivateOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const loggedInUserId = getUserIdFromToken();
  const isSelf = currentUser.id === loggedInUserId;
  // TI gerencia usuários mas não pode promover a ADMIN nem alterar administradores (at-25).
  const isTi = useIsTi();

  useEffect(() => {
    if (!isOpen) return;

    // at-81: perfis ALÉM do principal; semeados com o que o usuário já tem.
    //
    // at-95 (BUG-R95): `normalizeUserType` (não `.toUpperCase()`) — o backend devolve `papeis`
    // em ENUM cru, mas a rota conviveu por um tempo sem o campo, e a janela entre deploys de
    // backend/frontend pode servir uma resposta antiga. `.toUpperCase()` quebrava qualquer
    // papel de nome composto ("Chefe de Oficina" → "CHEFE DE OFICINA", que não é valor do
    // enum) — o save seguinte tomava 400. `normalizeUserType` já cobre as duas formas.
    setPerfisAdicionais(
      ((currentUser as any).papeis ?? [])
        .map((p: string) => normalizeUserType(String(p)))
        .filter((p: string) => p !== normalizeUserType(currentUser.userType)),
    );
    setUpdatedUser({
      fullName: currentUser.fullName || '',
      email: currentUser.email || '',
      username: currentUser.username || '',
      password: '',
      confirmPassword: '',
      userType: normalizeUserType(currentUser.userType),
      codigoVoluntario:
        currentUser.codigoVoluntario !== null &&
        currentUser.codigoVoluntario !== undefined
          ? String(currentUser.codigoVoluntario)
          : '',
      codigoRegional:
        currentUser.codigoRegional !== null &&
        currentUser.codigoRegional !== undefined
          ? String(currentUser.codigoRegional)
          : '',
    });
    setIsActive(!currentUser.deletedAt);
  }, [isOpen, currentUser]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedNode = event.target as Node;
      const isOutsideModal =
        modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar =
        sidebarRef?.current && !sidebarRef.current.contains(clickedNode);

      // O ConfirmModal de desativação é irmão do modal (fora do modalRef):
      // sem esta guarda, o mousedown no botão "Desativar" fecharia/desmontaria
      // tudo antes do click ser processado e a desativação nunca executaria.
      if (isOutsideModal && isOutsideSidebar && !isSaving && !isConfirmDeactivateOpen) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onCancel, sidebarRef, isSaving, isConfirmDeactivateOpen]);

  const normalizeNullableNumber = (
    value: string,
    fieldLabel: string,
  ): number | null => {
    const trimmed = value.trim();

    if (!trimmed) return null;

    const parsed = Number(trimmed);

    if (Number.isNaN(parsed)) {
      throw new Error(`${fieldLabel} deve ser numérico.`);
    }

    return parsed;
  };

  const executeUpdate = async (fullName: string, email: string, username: string, wasActive: boolean) => {
    try {
      const updateUserDto = {
        fullName,
        email,
        username,
        userType: updatedUser.userType,
        // at-81: lista DECLARATIVA — o principal sempre entra, e o backend valida a combinação
        // sobre o estado resultante (perfis herdados de grupo não entram aqui).
        papeis: [updatedUser.userType, ...perfisAdicionais],
        codigoVoluntario: normalizeNullableNumber(
          updatedUser.codigoVoluntario,
          'Código do voluntário',
        ),
        codigoRegional: normalizeNullableNumber(
          updatedUser.codigoRegional,
          'Código da regional',
        ),
        ...(updatedUser.password
          ? {
              password: updatedUser.password,
              confirmPassword: updatedUser.confirmPassword,
            }
          : {}),
      };

      setIsSaving(true);

      let savedUser = await updateUser(currentUser.id, updateUserDto);

      if (isActive !== wasActive) {
        if (isActive) {
          await restoreUser(currentUser.id);
        } else {
          await deleteUser(currentUser.id);
        }
        savedUser = await getUserById(currentUser.id);
      }

      setUser(savedUser as User);
      toast.success('Informações atualizadas com sucesso!');
      onCancel();
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao atualizar as informações.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      const fullName = updatedUser.fullName.trim();
      const email = updatedUser.email.trim();
      const username = updatedUser.username.trim();

      if (!fullName || !email || !username) {
        throw new Error('Nome, e-mail e nome de usuário não podem ser vazios.');
      }

      if (
        updatedUser.password &&
        updatedUser.password !== updatedUser.confirmPassword
      ) {
        throw new Error('As senhas não coincidem.');
      }

      const wasActive = !currentUser.deletedAt;
      if (isActive !== wasActive && !isActive) {
        setIsConfirmDeactivateOpen(true);
        return;
      }

      await executeUpdate(fullName, email, username, wasActive);
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao atualizar as informações.');
    }
  };

  const handleConfirmDeactivate = async () => {
    setIsConfirmDeactivateOpen(false);
    const fullName = updatedUser.fullName.trim();
    const email = updatedUser.email.trim();
    const username = updatedUser.username.trim();
    const wasActive = !currentUser.deletedAt;
    await executeUpdate(fullName, email, username, wasActive);
  };

  const handleCancelDeactivate = () => {
    setIsConfirmDeactivateOpen(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="edit-car-modal-overlay update-user-modal-specific">
        <div className="edit-car-modal-content" ref={modalRef}>
          <div className="lightbox-update-user">
            <form className="LightboxLogin" onSubmit={handleUpdateUser}>
              <div className="EditarCarro">Editar informações do usuário</div>

              <div className="Frame1000004010">
                <div className="Frame1000003994">
                  <div className="Campo">
                    <div className="CampoTitulo">Nome Completo</div>
                    <div className="CampoInput">
                      <input
                        className="Input"
                        type="text"
                        name="fullName"
                        value={updatedUser.fullName}
                        onChange={(e) =>
                          setUpdatedUser({
                            ...updatedUser,
                            fullName: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="Campo">
                    <div className="CampoTitulo">Nome de usuário (Username)</div>
                    <div className="CampoInput">
                      <input
                        className="Input"
                        type="text"
                        name="username"
                        value={updatedUser.username}
                        onChange={(e) =>
                          setUpdatedUser({
                            ...updatedUser,
                            username: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="Frame1000004010">
                <div className="Frame1000003994">
                  <div className="Campo">
                    <div className="CampoTitulo">E-mail</div>
                    <div className="CampoInput">
                      <input
                        className="Input"
                        type="email"
                        name="email"
                        value={updatedUser.email}
                        onChange={(e) =>
                          setUpdatedUser({
                            ...updatedUser,
                            email: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="Campo">
                    <div className="CampoTitulo">Tipo de Usuário</div>
                    <div className="CampoInput">
                      <select
                        name="userType"
                        value={updatedUser.userType}
                        onChange={(e) =>
                          setUpdatedUser({
                            ...updatedUser,
                            userType: e.target.value,
                          })
                        }
                        className="Input"
                      >
                        <option value="">Selecione...</option>
                        {/* Ordem alfabética pelo RÓTULO (pedido do Victor, 25/09/2026). */}
                        {(!isTi || normalizeUserType(currentUser.userType) === 'ADMIN') && (
                          <option value="ADMIN">ADMIN</option>
                        )}
                        <option value="ADMINISTRACAO">Administração</option>
                        <option value="CHEFE_DE_OFICINA">Chefe de Oficina</option>
                        <option value="COLABORADOR">Colaborador</option>
                        <option value="COMISSIONAMENTO">Comissionamento</option>
                        <option value="COMPRA">Compra</option>
                        <option value="CONSULTOR">Consultor</option>
                        <option value="CONTAC">Contac</option>
                        <option value="ESTOQUE">Estoque</option>
                        <option value="EVENTOS">Eventos</option>
                        <option value="GESTAO_COMPRAS">Gestão de Compras</option>
                        <option value="INSPETOR">Inspetor</option>
                        <option value="TELAO">Telão</option>
                        <option value="ORCAMENTO">Orçamento</option>
                        <option value="RASTREADOR">Rastreador</option>
                        <option value="RECEPCAO">Recepção</option>
                        <option value="REGIONAL">Regional</option>
                        <option value="RELACIONAMENTO">Relacionamento</option>
                        <option value="TI">TI</option>
                      </select>
                    </div>
                  </div>

                  <div className="Campo">
                    <PerfisAdicionais
                      papelPrincipal={updatedUser.userType}
                      selecionados={perfisAdicionais}
                      onChange={setPerfisAdicionais}
                      ocultarAdmin={isTi && normalizeUserType(currentUser.userType) !== 'ADMIN'}
                    />
                  </div>
                </div>
              </div>

              <div className="Frame1000004010">
                <div className="Frame1000003994">
                  <div className="Campo">
                    <div className="CampoTitulo">Senha</div>
                    <div className="CampoInput">
                      <input
                        className="Input"
                        type="password"
                        name="password"
                        autoComplete="new-password"
                        value={updatedUser.password}
                        onChange={(e) =>
                          setUpdatedUser({
                            ...updatedUser,
                            password: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="Campo">
                    <div className="CampoTitulo">Confirmar Senha</div>
                    <div className="CampoInput">
                      <input
                        className="Input"
                        type="password"
                        name="confirmPassword"
                        autoComplete="new-password"
                        value={updatedUser.confirmPassword}
                        onChange={(e) =>
                          setUpdatedUser({
                            ...updatedUser,
                            confirmPassword: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="Frame1000004010">
                <div className="Frame1000003994">
                  <div className="Campo">
                    <div className="CampoTitulo">Código do Voluntário</div>
                    <div className="CampoInput">
                      <input
                        className="Input"
                        type="text"
                        inputMode="numeric"
                        name="codigoVoluntario"
                        value={updatedUser.codigoVoluntario}
                        onChange={(e) =>
                          setUpdatedUser({
                            ...updatedUser,
                            codigoVoluntario: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="Campo">
                    <div className="CampoTitulo">Código da Regional</div>
                    <div className="CampoInput">
                      <input
                        className="Input"
                        type="text"
                        inputMode="numeric"
                        name="codigoRegional"
                        value={updatedUser.codigoRegional}
                        onChange={(e) =>
                          setUpdatedUser({
                            ...updatedUser,
                            codigoRegional: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="Frame1000004010">
                <div className="Frame1000003994">
                  <div className="Campo">
                    <div className="CampoTitulo">Status do Usuário</div>
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={isActive}
                          disabled={isSelf || isSaving}
                          onChange={(e) => setIsActive(e.target.checked)}
                        />
                        <span className="slider" />
                        <span className="toggle-label" style={{ color: 'var(--tema-texto, #1A1C1B)', fontWeight: 'bold' }}>
                          {isActive ? 'Ativo' : 'Inativo'}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <VinculoNasajonDoUsuario userId={currentUser.id} />

              <div className="Frame1000003998">
                <div className="CtaBigEditar cancel" onClick={onCancel}>
                  <button
                    className="CustomFileCancel"
                    type="button"
                    disabled={isSaving}
                  >
                    Cancelar
                  </button>
                </div>

                <button
                  className="CtaBigEditar save"
                  type="submit"
                  disabled={isSaving}
                >
                  {isSaving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ConfirmModal
        isOpen={isConfirmDeactivateOpen}
        message={`Tem certeza que deseja desativar o usuário "${updatedUser.fullName || updatedUser.username}"?`}
        confirmLabel="Desativar"
        onConfirm={handleConfirmDeactivate}
        onCancel={handleCancelDeactivate}
      />
    </>
  );
};

export default UpdateOtherUserModal;
